import React, { useState, useEffect, useRef, useMemo } from 'react'
import { createBlobFromURL } from "../templates/VideoDrawModal"
import { sleep } from "../functions/sleep"
import { range } from "../functions/range"

type videoSizeType = {
    width: number,
    height: number
}

type blobListType = ( Blob | undefined )

type Props = {
    videoFile: File,
    captureWidth: number,
    CaptureImageNumber: number
}

export const useCaptureList = ( {
    videoFile,
    captureWidth,
    CaptureImageNumber
}: Props ) => {

    const [ videoBlobURL, setVideoBlobURL ] = useState( window.URL.createObjectURL( videoFile ) );
    const [ captureBlobList, setCaptureBlobList ] = useState<blobListType[] | null>( null );
    const [ videoLoadEnd, setVideoLoadEnd ] = useState( false );
    const [ videoDuration, setVideoDuration ] = useState( 0 );
    const [ videoSize, setVideoSize ] = useState<videoSizeType | null>( null );
    const [ intervalID, setIntervalID ] = useState<NodeJS.Timeout | null>( null );
    const [ renderTriggerCaptureNumber, setRenderTriggerCaptureNumber ] = useState( 0 );

    const renderTriggerCaptureNumberRef = useRef( renderTriggerCaptureNumber )
    useEffect( () => {
        renderTriggerCaptureNumberRef.current = renderTriggerCaptureNumber
    }, [ renderTriggerCaptureNumber ] )

    const intervalIDRef = useRef( intervalID )
    useEffect( () => {
        intervalIDRef.current = intervalID
    }, [ intervalID ] )

    useEffect( () => {
        return () => { if ( intervalID ) clearInterval( intervalID ) }
    }, [] )


    const videoRef = useRef<HTMLVideoElement>( null )
    const canvasRef = useRef<HTMLCanvasElement>( null )

    useEffect( () => {
        init()
    }, [ videoFile ] )

    const init = () => {
        setVideoBlobURL( window.URL.createObjectURL( videoFile ) )
        setVideoDuration( 0 )
        setCaptureBlobList( null )
        setVideoLoadEnd( false )
        intervalID && clearInterval( intervalID )
        setRenderTriggerCaptureNumber( 0 )
        startDetectVideoLoadEnd()
    }

    const startDetectVideoLoadEnd = () => {
        const newIntervalID = setInterval( detectVideoLoadEndandSetVideoSize, 100 )
        setIntervalID( newIntervalID )
    }

    const detectVideoLoadEndandSetVideoSize = () => {
        if ( !videoRef.current ) return
        if ( videoRef.current && 4 <= videoRef.current.readyState ) {
            setVideoDuration( videoRef.current.duration )
            setVideoLoadEnd( true )
            intervalIDRef.current && clearInterval( intervalIDRef.current )
            setIntervalID( null )
        }
    }

    useEffect( () => {
        videoLoadEnd && setSizeOfVideo()
    }, [ videoLoadEnd ] )

    useEffect( () => {
        videoSize && videoRef.current && getCapture( videoRef.current.duration )
    }, [ videoSize ] )

    const getCapture = async ( duration: number ) => {
        await extractCaptureList( getCaptureTimeList() )
    }

    const setSizeOfVideo = () =>
        videoRef.current && videoLoadEnd && setVideoSize( {
            width: videoRef.current.videoWidth,
            height: videoRef.current.videoHeight,
        } )

    function getCaptureTimeList () {
        const interval = videoDuration / ( CaptureImageNumber - 1 )
        const newCaptureTimeList = range( 0, CaptureImageNumber ).map( sec => sec * interval )
        return newCaptureTimeList
    }

    const extractCaptureList = async ( captureTimeList: number[] ) => {
        const newCaptureList: blobListType[] = []
        await sleep( 100 )
        for ( let i = 0; i < captureTimeList.length; i++ ) {
            const blob = await drawCaptureOnCanvas( captureTimeList[ i ] )
            newCaptureList.push( blob )
        }
        setRenderTriggerCaptureNumber( 0 )
        newCaptureList.every( blob => !!blob ) && setCaptureBlobList( newCaptureList )
    }

    const drawCaptureOnCanvas = async ( time: number ) => {
        if ( !videoRef.current ) return
        videoRef.current.currentTime = time
        await sleep( 100 )
        const canvas = canvasRef.current
        if ( !canvas ) return undefined
        if ( !canvas.getContext ) return undefined
        const ctx = canvas.getContext( "2d" ) 
        if ( !ctx) return undefined
        if ( !videoSize ) return undefined
        ctx.drawImage( videoRef.current, 0, 0, videoSize.width, videoSize.height );
        setRenderTriggerCaptureNumber( renderTriggerCaptureNumberRef.current + 1 )
        await sleep( 100 )
        const imageBlob = getCanvasImageBlob()
        return imageBlob
    }

    const getCanvasImageBlob = () => {
        if ( !canvasRef.current ) return undefined
        const canvasImageURL = canvasRef.current.toDataURL( "image/jpeg" )
        const blob = createBlobFromURL( canvasImageURL )
        return blob
    }

    return useMemo( () =>
        captureBlobList ?
            ( {
                finished: true,
                captureList: captureBlobList.map( ( captureBlob, index ) =>
                    <img
                        style={ { width: captureWidth } }
                        src={ window.URL.createObjectURL( captureBlob ) }
                        key={ String( index ) } />
                )
            } )
            :
            ( {
                finished: false,
                drawCanvas:
                    <div style={ { height: 0 } }>
                        <video
                            ref={ videoRef }
                            style={ {
                                display: "none"
                            } }
                            src={ videoBlobURL }
                            preload="metadata"
                        />
                        { videoSize &&
                            <canvas
                                width={ videoSize.width }
                                height={ videoSize.height }
                                // style={ { width: videoSize.width, height: videoSize.height } }
                                style={ { width: 0, height: 0 } }
                                ref={ canvasRef }>
                            </canvas> }
                    </div>
            } ), [ captureBlobList, videoFile, videoSize, renderTriggerCaptureNumber ] );
}
